import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { ativar, findAll, remove, save } from "./api-cardapio";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Container from "../UI/Container";
import CardapioForm from "./CardapioForm";
import CardapioTable from "./CardapioTable";
import CardapioSemanaContainer from "./Semana/CardapioSemanaContainer";

const porDescricao = (a, b) => (a.descricao ?? "").localeCompare(b.descricao ?? "", "pt-BR");

const CardapioContainer = () => {
  const [cardapios, setCardapios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cardapio, setCardapio] = useState(null);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [ativando, setAtivando] = useState(null);

  useEffect(() => {
    findAll()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar os cardápios.");
        } else if (Array.isArray(data)) {
          setCardapios([...data].sort(porDescricao));
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleSave = (persist) => {
    const eNovo = !persist.codigo;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar o cardápio.");
          return;
        }
        setCardapios((prevState) =>
          (eNovo ? [...prevState, data] : prevState.map((c) => (c.codigo === data.codigo ? data : c))).sort(
            porDescricao,
          ),
        );
        setEditando(null);
        // Um cardápio novo abre direto para cadastrar as semanas.
        setCardapio(data);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleAtivar = (alvo) => {
    setAtivando(alvo.codigo);
    ativar(alvo.codigo)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível ativar o cardápio.");
          return;
        }
        // Só um cardápio fica ativo por vez.
        setCardapios((prevState) => prevState.map((c) => (c.codigo === alvo.codigo ? data : { ...c, ativo: false })));
        setCardapio((prevState) => (prevState?.codigo === alvo.codigo ? data : prevState));
        success(`"${alvo.descricao}" agora é o cardápio ativo.`);
      })
      .finally(() => setAtivando(null));
  };

  const handleRemove = () =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover o cardápio "${cardapio.descricao}"? As refeições cadastradas nele também serão removidas.`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(cardapio.codigo).then((data) => {
              if (!data || data.error) {
                errorMessage(data?.message || "Não foi possível remover o cardápio.");
                return;
              }
              setCardapios((prevState) => prevState.filter((c) => c.codigo !== cardapio.codigo));
              setCardapio(null);
              success("Cardápio removido.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  return (
    <Container>
      <CardapioForm cardapio={editando} onClose={() => setEditando(null)} onSave={handleSave} isSaving={isSaving} />

      {cardapio ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={3}>
            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
              <Tooltip title="Voltar para os cardápios">
                <IconButton edge="start" onClick={() => setCardapio(null)}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Box minWidth={0}>
                <Typography variant="h6" noWrap>
                  {cardapio.descricao}
                </Typography>
                {cardapio.ativo ? (
                  <Chip size="small" color="success" variant="outlined" label="Ativo" />
                ) : (
                  <Button size="small" disabled={ativando !== null} onClick={() => handleAtivar(cardapio)}>
                    Tornar ativo
                  </Button>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Renomear">
                <IconButton onClick={() => setEditando(cardapio)}>
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remover cardápio">
                <IconButton color="error" onClick={handleRemove}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <CardapioSemanaContainer key={cardapio.codigo} cardapio={cardapio} />
        </>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            mb={3}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Menu Integral">
                <IconButton edge="start" component={Link} to="/integral">
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography variant="h6">Cardápio</Typography>
                <Typography variant="body2" color="text.secondary">
                  Cadastre os cardápios do integral. O cardápio ativo é o que alunos e responsáveis veem.
                </Typography>
              </Box>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ eNovo: true })}>
              Novo Cardápio
            </Button>
          </Stack>
          <CardapioTable
            cardapios={cardapios}
            isLoading={isLoading}
            ativando={ativando}
            onSelect={setCardapio}
            onAtivar={handleAtivar}
          />
        </>
      )}
    </Container>
  );
};

export default CardapioContainer;
